import React, { useCallback, useState } from "react";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiCodeBlock,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { useApi } from "src/api";
import { useAnketaAuth } from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { useToast } from "src/notifications";

const i18n = "anketa.opsanio.appliance.flyout.hardware.switchConfig";

/**
 * Puts ONE switch's FS configuration on the clipboard, rendered server-side
 * from the reference template for that switch's position with the VPC name,
 * the customer/CPE VLAN ids and the switch's own addresses filled in.
 *
 * One button per switch rather than one per appliance: an MLAG pair runs two
 * configurations that differ in hostname, CPE management address and peer-link
 * address, so "the appliance's switch configuration" is not a thing that exists
 * once the customer has two of them. `switchIndex` is the switch's 1-based
 * position in the questionnaire's hardware selection - the same position its
 * `-m0-sws-00N` hostname is numbered from - so the button on a device row and
 * the configuration that device ends up running name the same switch.
 *
 * Fetched on click rather than with the tab: it is a whole configuration file
 * and most visits to this tab are not about the switch.
 *
 * Copying can fail for reasons that have nothing to do with the config - a
 * browser that refuses clipboard writes outside a direct gesture, an insecure
 * origin, a denied permission - so a failed write falls back to showing the
 * configuration in a modal. The operator is never left with a button that did
 * nothing and no way to get at the text.
 */
export const SwitchConfigCopyButton = ({
  catalogId,
  questionnaireId,
  switchIndex = 1,
  /** Shown on the toast and the fallback modal so a pair stays tellable apart. */
  deviceName,
}: {
  catalogId: string;
  questionnaireId: string;
  switchIndex?: number;
  deviceName?: string;
}): JSX.Element => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();

  const [loading, setLoading] = useState<boolean>(false);
  /** Set only when the clipboard write failed - then it is shown to copy by hand. */
  const [fallback, setFallback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The hostname the fetched configuration actually sets, once it is known. */
  const [hostname, setHostname] = useState<string | null>(null);

  const copy = useCallback(async () => {
    setLoading(true);
    setError(null);
    const response = await Get<any>({
      path: `v2/documentation/switch-config/${encodeURIComponent(
        catalogId,
      )}/${encodeURIComponent(questionnaireId)}?switch=${encodeURIComponent(
        switchIndex,
      )}`,
      responseType: "json",
      token: keycloak?.token,
    });
    setLoading(false);

    // The API client never throws on HTTP errors - `success` is the check. A
    // questionnaire with no VLANs answers 422 and names what is missing; that
    // message is more useful than a generic failure, so it is shown as-is.
    if (!response.success) {
      setError(
        (response.data as any)?.error ??
          response.error?.message ??
          translate(`${i18n}.error`, {
            defaultMessage: "The switch configuration could not be loaded.",
          }),
      );
      return;
    }

    const config: string = (response.data as any)?.data?.config ?? "";
    setHostname((response.data as any)?.data?.hostname ?? null);
    if (config.trim() === "") {
      setError(
        translate(`${i18n}.empty`, {
          defaultMessage: "The switch configuration came back empty.",
        }),
      );
      return;
    }

    try {
      await navigator.clipboard.writeText(config);
    } catch {
      setFallback(config);
      return;
    }

    addToast({
      title: translate(`${i18n}.copied.title`, {
        defaultMessage: "Switch configuration copied",
      }),
      color: "success",
      text: (
        <p>
          {translate(`${i18n}.copied.body`, {
            values: {
              switch:
                (response.data as any)?.data?.hostname ||
                deviceName ||
                `${switchIndex}`,
            },
            defaultMessage:
              "The configuration for {{switch}} is on the clipboard.",
          })}
        </p>
      ),
    });
  }, [
    Get,
    addToast,
    catalogId,
    deviceName,
    keycloak?.token,
    questionnaireId,
    switchIndex,
    translate,
  ]);

  return (
    <>
      <EuiButton
        size="s"
        iconType="copyClipboard"
        onClick={copy}
        isLoading={loading}
      >
        {translate(`${i18n}.button`, {
          defaultMessage: "Copy configuration",
        })}
      </EuiButton>

      {error && (
        <>
          <EuiSpacer size="s" />
          <EuiCallOut size="s" color="danger" iconType="alert" title={error} />
        </>
      )}

      {fallback !== null && (
        <EuiModal onClose={() => setFallback(null)} style={{ minWidth: 720 }}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              <h2>
                {hostname || deviceName
                  ? translate(`${i18n}.modal.titleFor`, {
                      values: { switch: hostname || deviceName },
                      defaultMessage: "Configuration for {{switch}}",
                    })
                  : translate(`${i18n}.modal.title`, {
                      defaultMessage: "Switch configuration",
                    })}
              </h2>
            </EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody>
            <EuiText size="s">
              <p>
                {translate(`${i18n}.modal.body`, {
                  defaultMessage:
                    "The browser refused to write to the clipboard. Copy the configuration from here instead.",
                })}
              </p>
            </EuiText>
            <EuiSpacer size="s" />
            <EuiCodeBlock
              language="text"
              fontSize="s"
              paddingSize="s"
              overflowHeight={420}
              isCopyable
            >
              {fallback}
            </EuiCodeBlock>
          </EuiModalBody>
          <EuiModalFooter>
            <EuiButtonEmpty onClick={() => setFallback(null)}>
              {translate("anketa.create.action.cancel", {
                defaultMessage: "Close",
              })}
            </EuiButtonEmpty>
          </EuiModalFooter>
        </EuiModal>
      )}
    </>
  );
};

export default SwitchConfigCopyButton;
